import { Box, Divider, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { PremiseConfig } from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { templateSubLabel, type TemplateSubSection } from "../PremiseSidebar";
import LivePreview from "./LivePreview";
import TemplateSubEditor, { useTemplateApps } from "./TemplateSubEditor";
import { PageHeader } from "../PageHeader";
import type { PremiseCommit } from "../../context/PremiseWorkspace";

interface Props {
  premise: PremiseConfig;
  /** Which sub-section to show under the pinned preview. */
  sub: TemplateSubSection;
  /** Saves each change and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
  /** Move to another sub-section - lets Screens link into each editor. */
  onSelectSub?: (sub: TemplateSubSection) => void;
}

/**
 * Which TV screen the preview should open on for each Component tab.
 *
 * Only the tabs whose key IS a screen id: the rest (Colours, Branding, Pick
 * template) are about the whole template, and the home screen shows those best.
 * A wrong id costs nothing - the TV falls back to rendering that screen's
 * absence, exactly as an unreachable screen would.
 */
export const PREVIEW_SCREEN: Partial<Record<TemplateSubSection, string>> = {
  apps: "apps",
  radio: "radio",
  info: "info",
  menu: "menu",
  vilmer: "vilmer",
  wifi: "wifi",
  welcome: "welcome",
  relatives: "relatives",
  instagram: "instagram",
  weather: "weather",
  custom: "custom",
};

/**
 * The bespoke-template editor. A live preview is pinned at the top; below it,
 * only the controls for the selected sub-section render (chosen from the
 * sidebar). Switches and reorders save at once; forms open in a drawer with
 * their own Save (see TemplateSubEditor). Every save toasts.
 */
export default function TemplateEditor({
  premise,
  sub,
  onCommit,
  onSelectSub,
}: Props) {
  const { t } = useTranslation(["templates", "premises"]);
  const binding = premise.scheme;
  const manifest = binding ? SCHEME_MANIFESTS[binding.key] : undefined;
  const apps = useTemplateApps(premise);

  // The page is headed by what the operator CALLS this screen, so a renamed
  // screen reads the same in the nav row they clicked and on the page it opens.
  const title = templateSubLabel(
    { key: sub, label: t(`premises:premiseSidebar.templateSubs.${sub}`) },
    manifest?.knobs,
    binding?.knobs
  );

  return (
    <Stack spacing={2} data-testid="template-editor">
      {/* The slots editor is reached from Promotions, not from the template
          picker, so it wears that product's name - the page an operator opened
          and the eyebrow they read should agree. */}
      <PageHeader
        eyebrow={
          sub === "slots"
            ? t("templateEditor.eyebrowPromotions")
            : t("templateEditor.eyebrowTemplate")
        }
        title={title}
      />
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.default",
          pb: 1.5,
        }}
      >
        <LivePreview premise={premise} screen={PREVIEW_SCREEN[sub]} />
      </Box>
      <Divider />
      <TemplateSubEditor
        premise={premise}
        sub={sub}
        onCommit={onCommit}
        apps={apps}
        onSelectSub={onSelectSub}
      />
    </Stack>
  );
}
